<template>
    <div class="ppv2-flex ppv2-flex-col ppv2-gap-8">
        <form @submit.prevent="save" class="ppv2-flex ppv2-flex-col ppv2-gap-8">
            <!-- Reservation Section -->
            <SectionCard :title="$t('website_settings.reservation.title')" :hideToggle="true">
                <template #header-right>
                    <a
                        :href="reservationUrl"
                        target="_blank"
                        class="ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-ml-2 ppv2-underline ppv2-text-black ppv2-text-opacity-90 hover:ppv2-text-opacity-100 ppv2-transition-colors"
                    >
                        <Link :size="15" />
                        <span :style="{ fontSize: normalizeFontSize(14) }">
                            {{ reservationUrl }}
                        </span>
                    </a>
                </template>

                <div class="ppv2-flex ppv2-flex-col lg:ppv2-flex-row ppv2-gap-12 ppv2-mt-4">
                    <!-- Left: Form Fields -->
                    <div class="ppv2-flex-1 ppv2-flex ppv2-flex-col ppv2-gap-6">
                        <div>
                            <CustomInput
                                v-model="form.reservation_title"
                                :required="true"
                                :label="$t('website_settings.reservation.main_title')"
                                customLabelClass="poppinsLight"
                                :placeholder="$t('website_settings.reservation.description_placeholder')"
                                :disabled="false"
                            />
                        </div>
                        <div>
                            <BaseSelect
                                v-model="form.reservation_order"
                                :required="true"
                                customLabelClass="poppinsLight"
                                classAdded="ppv2-mb-[20px]"
                                :label="$t('website_settings.reservation.display_order')"
                                :options="displayOrderOptions"
                                :disabled="false"
                                fixed-dropdown
                            />
                        </div>
                        <div class="ppv2-flex ppv2-items-end" :style="{ gap: normalizeSize(16, true) }">
                            <div class="ppv2-flex-1">
                                <BaseColorInput
                                    v-model="form.reservation_color"
                                    :required="true"
                                    :label="$t('website_settings.reservation.background_color')"
                                    :iconRight="iconRight"
                                    customClassLabel="poppinsLight"
                                    customContainerClass="ppv2-flex ppv2-flex-wrap ppv2-gap-3 ppv2-align-items-center ppv2-justify-between"
                                    :disabled="false"
                                >
                                </BaseColorInput>
                            </div>
                        </div>
                    </div>

                    <!-- Right: Preview Decor -->
                    <div
                        class="ppv2-flex-[0.6] ppv2-hidden lg:ppv2-flex ppv2-items-center ppv2-justify-center ppv2-relative ppv2-top-[20px]"
                    >
                        <img src="/media/siteInternet/apropos.png" class="ppv2-max-w-full ppv2-h-[260px]" />
                    </div>
                </div>
            </SectionCard>

            <!-- About Section -->
            <SectionCard :title="$t('website_settings.about.title')" :hideToggle="true">
                <template #header-right>
                    <a
                        :href="aboutUrl"
                        target="_blank"
                        class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-ml-2 ppv2-underline ppv2-text-black ppv2-text-opacity-90 hover:ppv2-text-opacity-100 ppv2-transition-colors"
                    >
                        <Link :size="15" />
                        <span :style="{ fontSize: normalizeFontSize(14) }">
                            {{ aboutUrl }}
                        </span>
                    </a>
                </template>

                <div class="ppv2-flex ppv2-flex-col ppv2-gap-8 ppv2-mt-4">
                    <div>
                        <CustomInput
                            v-model="form.title"
                            :label="$t('website_settings.about.main_title')"
                            customLabelClass="poppinsLight"
                            :required="aboutIsEnabled || titleRequired"
                            :placeholder="$t('website_settings.about.description_placeholder')"
                            :errorText="errors.title"
                        />
                    </div>

                    <div>
                        <BaseTextarea
                            v-model="form.description"
                            :label="$t('website_settings.about.content')"
                            customLabelClass="poppinsLight"
                            :required="aboutIsEnabled || descriptionRequired"
                            :use-editor="true"
                            :rows="11"
                            :placeholder="$t('website_settings.about.description_placeholder')"
                            :error="errors.description"
                        />
                    </div>

                    <div>
                        <CustomInputUpload
                            v-model="form.image_url"
                            @input="onMainImageSelected"
                            :label="$t('website_settings.about.cover_image')"
                            customLabelClass="poppinsLight"
                            :specificTaille="$t('website_settings.about.cover_image_recommendation')"
                        />
                    </div>

                    <div>
                        <BaseLabel :label="$t('website_settings.about.gallery_section')" class="ppv2-mb-4" />
                        <div class="ppv2-flex ppv2-flex-col" :style="{ gap: normalizeSize(16, true) }">
                            <CustomInputUpload
                                v-model="dummyGallery"
                                @input="handleGalleryUpload"
                                :multiple="true"
                                customLabelClass="poppinsLight"
                                :specificTaille="$t('website_settings.about.gallery_image_recommendation')"
                            />

                            <!-- Gallery Grid -->
                            <div
                                v-if="galleryImages.length > 0"
                                class="ppv2-grid ppv2-grid-cols-2 md:ppv2-grid-cols-5"
                                :style="{ gap: normalizeSize(16, true), marginTop: normalizeSize(16, true) }"
                            >
                                <div v-for="(img, index) in galleryImages" :key="index" class="ppv2-relative ppv2-group">
                                    <div
                                        class="ppv2-aspect-[440/320] ppv2-rounded-2xl ppv2-overflow-hidden ppv2-border ppv2-border-gray-100"
                                    >
                                        <img :src="img.url" class="ppv2-w-full ppv2-h-full ppv2-object-cover" />
                                    </div>
                                    <button
                                        type="button"
                                        class="ppv2-absolute ppv2-top-2 ppv2-right-2 ppv2-text-red-400 hover:ppv2-text-red-600 ppv2-transition-colors"
                                        @click="removeGalleryImage(index)"
                                    >
                                        <Trash2 :size="18" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </SectionCard>
        </form>

        <!-- Delete Confirmation Dialog -->
        <CustomDialog v-model="showDeleteDialog" :defaultTitle="$t('common.confirm_delete')" noScroll>
            <template #content>
                <div class="ppv2-text-center ppv2-py-4">
                    <div class="ppv2-mb-4">
                        <TriangleAlert :size="48" class="ppv2-text-yellow-500 ppv2-inline-block" />
                    </div>
                    <p class="ppv2-text-gray-700 dark:ppv2-text-gray-300 ppv2-text-base">
                        {{ $t('website_settings.about.gallery_delete_confirm') }}
                    </p>
                </div>
            </template>
            <template #actions>
                <BaseButton
                    variant="outline"
                    @click="showDeleteDialog = false"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-sm !ppv2-min-w-[120px]`"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    variant="danger"
                    @click="confirmDeleteGalleryImage"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-sm !ppv2-min-w-[120px]`"
                >
                    {{ $t('common.confirm') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import SectionCard from './SectionCard.vue'
import BaseLabel from '../../../../Shared/BaseLabel.vue'
import CustomInput from '../../../../Inputs/CustomInput.vue'
import BaseTextarea from '../../../../Shared/BaseTextarea.vue'
import CustomInputUpload from '../../../../Inputs/CustomInputUpload.vue'
import CustomDialog from '../../../../Shared/CustomDialog.vue'
import BaseButton from '../../../../Shared/BaseButton.vue'
import BaseSelect from '../../../../Shared/BaseSelect.vue'
import BaseColorInput from '../../../../Shared/BaseColorInput.vue'
import { Link, Trash2, TriangleAlert, Star } from 'lucide-vue'
import normalizeSize from '../../../../../utils/normalizeSize'
import normalizeFontSize from '../../../../../utils/normalizeFontSize'

export default {
    name: 'TabAbout',
    components: {
        SectionCard,
        BaseLabel,
        CustomInput,
        BaseTextarea,
        CustomInputUpload,
        CustomDialog,
        BaseButton,
        BaseSelect,
        BaseColorInput,
        Link,
        Trash2,
        TriangleAlert,
        Star,
    },
    props: {
        data: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            saving: false,
            originalData: null,
            dummyGallery: null,
            iconRight: `<div class="ppv2-bg-[#7D2DBD] ppv2-rounded-full ppv2-p-1 ppv2-flex ppv2-items-center ppv2-justify-center ppv2-ml-1">
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="transparent" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-star"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                        </div>`,
            form: {
                // Reservation fields (stored in WebsiteSetting)
                reservation_title: '',
                reservation_order: 'experiences',
                reservation_color: '#FFFFFF',

                // About fields (stored in APropos)
                title: '',
                description: '',
                image_url: null,
                image_file: null,
                delete_image_file: false,
                missions: [], // Keep for compatibility if needed
            },
            galleryImages: [],
            showDeleteDialog: false,
            imageToDeleteIndex: null,
            errors: {},
        }
    },
    computed: {
        descriptionRequired() {
            return !!(this.form.title && String(this.form.title).trim())
        },
        titleRequired() {
            return !!(this.form.description && String(this.form.description).trim())
        },
        reservationUrl() {
            let url = this.data?.domain || window.location.origin
            return url.split('/back-office')[0].replace('-bo.', '.') + '/discover/reservation'
        },
        aboutUrl() {
            let url = this.data?.domain || window.location.origin
            return url.split('/back-office')[0].replace('-bo.', '.') + '/about-us'
        },
        // Reflète la règle SERVEUR (WebsiteController::storePropos) : section désactivée depuis
        // l'onglet Menu, ses champs ne sont plus exigés. Même convention de lecture que TabMenu,
        // et même défaut à activé quand le paramètre n'existe pas encore.
        aboutIsEnabled() {
            const setting = (this.data?.settings || []).find((s) => s.parameter === 'enabled_menu_propos')
            return !setting || setting.value === 'on' || setting.value === true
        },
        displayOrderOptions() {
            return [
                { value: 'experiences', label: this.$t('website_settings.reservation.display_orders.experiences_first') },
                { value: 'categories', label: this.$t('website_settings.reservation.display_orders.categories_first') },
            ]
        },
    },
    mounted() {
        this.initializeForm()
    },
    activated() {
        this.$root.$off('website-settings-trigger-save-pages', this.save)
        this.$root.$on('website-settings-trigger-save-pages', this.save)
    },
    deactivated() {
        this.$root.$off('website-settings-trigger-save-pages', this.save)
    },
    beforeDestroy() {
        this.$root.$off('website-settings-trigger-save-pages', this.save)
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        initializeForm() {
            // Load APropos data
            if (this.data?.propos) {
                const propos = this.data.propos
                this.form.title = propos.title || ''
                this.form.description = propos.description || ''
                this.form.image_url = propos.image || null

                // Parse gallery images
                if (propos.images_gallery) {
                    const images = propos.images_gallery.split(',').filter((img) => img.trim())
                    this.galleryImages = images.map((url) => ({ url: url.trim(), file: null, isExisting: true }))
                }
            }

            // Load WebsiteSettings for Reservation
            const settings = this.data?.settings || this.data?.currentSettings
            if (settings) {
                settings.forEach((s) => {
                    if (s.parameter === 'reservation_title') this.form.reservation_title = s.value
                    if (s.parameter === 'reservation_order') this.form.reservation_order = s.value
                    if (s.parameter === 'reservation_color') this.form.reservation_color = s.value
                })
            }

            if (this.data?.missions) {
                this.form.missions = this.data.missions.map((m) => ({
                    title: m.title || '',
                    description: m.description || '',
                }))
            }

            // Store original data for reset
            this.originalData = JSON.parse(JSON.stringify(this.form))
        },
        onMainImageSelected(file) {
            this.form.image_file = file
            if (file) {
                const reader = new FileReader()
                reader.onload = (e) => {
                    this.form.image_url = e.target.result
                    this.form.delete_image_file = false
                }
                reader.readAsDataURL(file)
            } else {
                this.form.delete_image_file = true
            }
        },
        handleGalleryUpload(file) {
            if (!file) return

            const reader = new FileReader()
            reader.onload = (e) => {
                this.galleryImages.push({ url: e.target.result, file: file, isExisting: false })
            }
            reader.readAsDataURL(file)

            // Re-reset dummy gallery for next upload
            this.dummyGallery = null
        },
        removeGalleryImage(index) {
            this.imageToDeleteIndex = index
            this.showDeleteDialog = true
        },
        confirmDeleteGalleryImage() {
            if (this.imageToDeleteIndex !== null) {
                this.galleryImages.splice(this.imageToDeleteIndex, 1)
                this.showDeleteDialog = false
                this.imageToDeleteIndex = null
            }
        },
        async save() {
            this.saving = true
            this.errors = {}
            this.$emit('save', { loading: true })
            try {
                // 1. Save APropos Data
                const proposData = new FormData()
                proposData.append('title', this.form.title)
                proposData.append('description', this.form.description)

                if (this.form.image_file) {
                    proposData.append('image', this.form.image_file)
                }
                if (this.form.delete_image_file) proposData.append('delete_image_file', 1)

                const existingImages = this.galleryImages.filter((img) => img.isExisting && !img.file).map((img) => img.url)
                proposData.append('existing_gallery_images', JSON.stringify(existingImages))

                this.galleryImages.forEach((img) => {
                    if (img.file) {
                        proposData.append('images_gallery[]', img.file)
                    }
                })

                // Missions
                this.form.missions.forEach((mission, index) => {
                    proposData.append(`title_mission[${index}]`, mission.title)
                    proposData.append(`mission_content[${index}]`, mission.description)
                })

                await axios.post('/back-office/website/apropos-settings/store', proposData)

                // 2. Save WebsiteSettings (Reservation)
                const settingsData = new FormData()
                settingsData.append('reservation_title', this.form.reservation_title)
                settingsData.append('reservation_order', this.form.reservation_order)
                settingsData.append('reservation_color', this.form.reservation_color)

                const settingsResponse = await axios.post('/back-office/website/website-settings', settingsData)
                this.$emit('save', { loading: false, success: true, message: settingsResponse.data?.message })
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.$emit('save', { loading: false })
                if (error.response && error.response.status === 422) {
                    this.errors = error.response.data.errors
                    this.$toast.error(this.$t('common.error_validate') || 'Erreur de validation')
                } else {
                    this.$toast.error(this.$t('website_settings.actions.save_error') || "Erreur lors de l'enregistrement")
                }
            } finally {
                this.saving = false
            }
        },
    },
}
</script>

<style scoped></style>
